import { ConstData } from '@/datas/ConstData';
import { useLangDataStore, useUserStore } from '@/utils/util_states';

export const Ev = {
    app_launch: 'app_launch',

    tut_welcome: 'tut_welcome',
    tut_ask_familiar: 'tut_ask_familiar',
    tut_yes: 'tut_yes',
    tut_no: 'tut_no',
    tut_intro_demo: 'tut_intro_demo',
    tut_puzzle_demo: 'tut_puzzle_demo',
    tut_congrats: 'tut_congrats',
    tut_final_welcome: 'tut_final_welcome',

    login_view: 'login_view',
    login_otp_req: 'login_otp_req',
    login_otp_resend: 'login_otp_resend',
    login_otp_verify: 'login_otp_verify',
    login_otp_error_invalid: 'login_otp_error_invalid',
    login_otp_error_expired: 'login_otp_error_expired',
    login_otp_error_network: 'login_otp_error_network',
    login_success: 'login_success',

    guest_browse_start: 'guest_browse_start',
    guest_action_triggered: 'guest_action_triggered',
    guest_login_converted: 'guest_login_converted',

    puzzles_view: 'puzzles_view',
    puzzle_click: 'puzzle_click',

    discover_view: 'discover_view',
    discover_click: 'discover_click',
    discover_pickup_click: 'discover_pickup_click',
    discover_pickup_view: 'discover_pickup_view',
    ai_create_cta_view: 'ai_create_cta_view',
    ai_create_cta_click: 'ai_create_cta_click',

    create_click: 'create_click',
    create_ai_fill: 'create_ai_fill',
    create_try_self: 'create_try_self',
    create_publish: 'create_publish',
    ai_puzzle_generation_start: 'ai_puzzle_generation_start',
    ai_puzzle_generation_success: 'ai_puzzle_generation_success',
    ai_puzzle_generation_fail: 'ai_puzzle_generation_fail',

    chat_turn_success: 'chat_turn_success',
    chat_send_recovered_error: 'chat_send_recovered_error',
    puzzle_complete: 'puzzle_complete',
    next_puzzle_view: 'next_puzzle_view',
    next_puzzle_action: 'next_puzzle_action',
    next_puzzle_start: 'next_puzzle_start',

    puzzle_give_up: 'puzzle_give_up',
    answer_reveal_view: 'answer_reveal_view',
    answer_reveal_confirm: 'answer_reveal_confirm',
    answer_reveal_leave: 'answer_reveal_leave',
    answer_reveal_close: 'answer_reveal_close',
    giveup_action: 'giveup_action',

    tour_start: 'tour_start',
    tour_step_view: 'tour_step_view',
    tour_step_complete: 'tour_step_complete',
    tour_close: 'tour_close',
    tour_complete: 'tour_complete',

    paywall_view: 'paywall_view',
    paywall_select: 'paywall_select',
    paywall_confirm: 'paywall_confirm',
    paywall_purchase_success: 'paywall_purchase_success',
    paywall_purchase_fail: 'paywall_purchase_fail',
    paywall_close: 'paywall_close',

    // Ads are App-only for now; the catalogue stays aligned for shared reporting.
    ad_reward_click: 'ad_reward_click',
    ad_reward_done: 'ad_reward_done',
    ad_reward_limit: 'ad_reward_limit',
    ad_reward_abort: 'ad_reward_abort',
} as const;

export type TrackEvent = (typeof Ev)[keyof typeof Ev];

const GUEST_BROWSED_KEY = 'guest_browsed';
const GUEST_LOGIN_REASON_KEY = 'guest_login_reason';
const GUEST_BROWSED_AT_KEY = 'guest_browsed_at';
const GUEST_ATTRIBUTION_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

type QueuedEvent = { row: Record<string, unknown>; attempts: number };

let sessionId = '';
let anonymousId = '';
let queue: QueuedEvent[] = [];
let retryTimer: ReturnType<typeof setTimeout> | null = null;
let flushing = false;

const FLUSH_INTERVAL = 10_000;
const FLUSH_SIZE = 20;
const MAX_RETRIES = 3;
const ANONYMOUS_ID_KEY = 'spg_analytics_anonymous_id';

function createId() {
    return crypto.randomUUID();
}

function getAnonymousId() {
    if (anonymousId) return anonymousId;
    try {
        const stored = localStorage.getItem(ANONYMOUS_ID_KEY);
        if (stored) {
            anonymousId = stored;
            return anonymousId;
        }
        anonymousId = createId();
        localStorage.setItem(ANONYMOUS_ID_KEY, anonymousId);
    } catch {
        anonymousId = createId();
    }
    return anonymousId;
}

function getDeviceType(): 'mobile' | 'desktop' | null {
    if (typeof window === 'undefined') return null;
    return window.matchMedia('(pointer: coarse)').matches ? 'mobile' : 'desktop';
}

function scheduleRetry(delayMs: number) {
    if (retryTimer) return;
    retryTimer = setTimeout(() => {
        retryTimer = null;
        void flush();
    }, delayMs);
}

export function initAnalytics() {
    if (sessionId) return;
    sessionId = createId();
    getAnonymousId();
    setInterval(() => { void flush(); }, FLUSH_INTERVAL);

    if (typeof document !== 'undefined') {
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'hidden') flushSync();
        });
    }
    if (typeof window !== 'undefined') {
        window.addEventListener('online', () => { void flush(); });
    }
}

export function track(event: TrackEvent, props?: Record<string, unknown>) {
    if (!sessionId) initAnalytics();

    const userState = useUserStore.getState();
    const langState = useLangDataStore.getState();
    queue.push({
        row: {
            session_id: sessionId,
            anonymous_id: getAnonymousId(),
            event,
            props: props ?? null,
            user_id: userState.userProfile?.id || null,
            device: getDeviceType(),
            platform: 'web',
            lang: langState.languageCode || null,
        },
        attempts: 0,
    });

    if (queue.length >= FLUSH_SIZE) void flush();
}

export function trackGuestBrowseStart(source: string) {
    track(Ev.guest_browse_start, { source });
    try {
        sessionStorage.setItem(GUEST_BROWSED_KEY, 'true');
        sessionStorage.removeItem(GUEST_LOGIN_REASON_KEY);
    } catch {
        // Session storage may be unavailable under strict browser privacy settings.
    }
    try {
        localStorage.setItem(GUEST_BROWSED_AT_KEY, String(Date.now()));
        localStorage.removeItem(GUEST_LOGIN_REASON_KEY);
    } catch {
        // Local storage may be unavailable under strict browser privacy settings.
    }
}

export function trackGuestLoginTrigger(reason: string, props?: Record<string, unknown>) {
    track(Ev.guest_action_triggered, { reason, source: reason, ...props });
    try {
        if (hasGuestBrowseJourney()) {
            sessionStorage.setItem(GUEST_LOGIN_REASON_KEY, reason);
        }
    } catch {
        // Session storage may be unavailable under strict browser privacy settings.
    }
    try {
        if (hasGuestBrowseJourney()) localStorage.setItem(GUEST_LOGIN_REASON_KEY, reason);
    } catch {
        // Local storage may be unavailable under strict browser privacy settings.
    }
}

export function hasGuestBrowseJourney(): boolean {
    try {
        if (sessionStorage.getItem(GUEST_BROWSED_KEY) === 'true') return true;
    } catch {
        // Fall through to local storage.
    }
    try {
        const startedAt = Number(localStorage.getItem(GUEST_BROWSED_AT_KEY));
        return Number.isFinite(startedAt) && Date.now() - startedAt <= GUEST_ATTRIBUTION_WINDOW_MS;
    } catch {
        return false;
    }
}

export function trackGuestLoginConverted(): boolean {
    if (!hasGuestBrowseJourney()) return false;
    let reason: string | null = null;
    try { reason = sessionStorage.getItem(GUEST_LOGIN_REASON_KEY); } catch { /* no-op */ }
    try { reason = reason || localStorage.getItem(GUEST_LOGIN_REASON_KEY); } catch { /* no-op */ }
    let startedAt = Number.NaN;
    try { startedAt = Number(localStorage.getItem(GUEST_BROWSED_AT_KEY)); } catch { /* no-op */ }
    const attributionAgeHours = Number.isFinite(startedAt)
        ? Math.max(0, Math.round((Date.now() - startedAt) / (60 * 60 * 1000)))
        : undefined;
    track(Ev.guest_login_converted, { reason: reason || 'voluntary_login', attribution_age_hours: attributionAgeHours });
    try { sessionStorage.removeItem(GUEST_BROWSED_KEY); sessionStorage.removeItem(GUEST_LOGIN_REASON_KEY); } catch { /* no-op */ }
    try { localStorage.removeItem(GUEST_BROWSED_AT_KEY); localStorage.removeItem(GUEST_LOGIN_REASON_KEY); } catch { /* no-op */ }
    return true;
}

export { flush as flushAnalytics };

async function flush() {
    if (flushing || queue.length === 0) return;
    if (retryTimer) {
        clearTimeout(retryTimer);
        retryTimer = null;
    }

    flushing = true;
    const batch = queue.splice(0, FLUSH_SIZE);
    try {
        await postBatch(batch.map((item) => item.row));
    } catch {
        const retryable = batch.filter((item) => item.attempts < MAX_RETRIES).map((item) => ({
            ...item,
            attempts: item.attempts + 1,
        }));
        if (retryable.length > 0) {
            queue.unshift(...retryable);
            scheduleRetry(Math.min(30_000, 1_000 * 2 ** retryable[0].attempts));
        }
    } finally {
        flushing = false;
        if (queue.length >= FLUSH_SIZE && !retryTimer) void flush();
    }
}

function flushSync() {
    if (flushing || queue.length === 0) return;
    const batch = queue.splice(0, FLUSH_SIZE);
    void postBatch(batch.map((item) => item.row), true).catch(() => {
        queue.unshift(...batch);
        scheduleRetry(1_000);
    });
}

/** 兼容尚未执行 anonymous_id 迁移的旧 event_log 表。 */
async function postBatch(rows: Record<string, unknown>[], keepalive = false) {
    const url = `${ConstData.supabase_url}/rest/v1/event_log`;
    const send = (payload: Record<string, unknown>[]) => fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            apikey: ConstData.supabase_anon_key,
            Authorization: `Bearer ${ConstData.supabase_anon_key}`,
            Prefer: 'return=minimal',
        },
        body: JSON.stringify(payload),
        keepalive,
    });

    let response = await send(rows);
    if (!response.ok) {
        const body = await response.text().catch(() => '');
        if (response.status === 400 && /anonymous_id|column .* does not exist/i.test(body)) {
            const legacyRows = rows.map(({ anonymous_id: _anonymousId, ...row }) => row);
            response = await send(legacyRows);
        } else {
            throw new Error(`HTTP ${response.status}`);
        }
    }
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
}
